import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import VendorForm from "./VendorForm";
import type { VendorFormData } from "../../../shared/schemas/outsourcing.schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Truck } from "lucide-react";

export interface VendorFormModalProps {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (newItem: { id: string; label: string; original?: any }) => void;
  onCancel?: () => void;
  initialData?: Partial<VendorFormData> & { name?: string; code?: string; [key: string]: any };
  lockedFields?: string[];
  params?: Record<string, any>;
}

export default function VendorFormModal({
  open,
  onClose,
  onSuccess,
  onCancel,
  initialData,
}: VendorFormModalProps) {
  const queryClient = useQueryClient();

  const handleClose = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };

  const handleSubmit = async (data: VendorFormData) => {
    try {
      const result = await trpcClient.vendors.create.mutate(data as any);

      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["combobox"] });
      await queryClient.invalidateQueries({ queryKey: ["vendor-lookup"] });
      await queryClient.invalidateQueries({
        queryKey: ["entity-select", "vendors"],
      });

      const label = result.name || result.shortName || result.code || "";
      toast.success(`Đã thêm nhà cung cấp: ${label}`);

      if (onSuccess) {
        onSuccess({
          id: result.id,
          label,
          original: result,
        });
      }
      if (onClose) onClose();
    } catch (err: any) {
      toast.error(err?.message || "Tạo nhà cung cấp thất bại");
    }
  };

  const content = (
    <div className="w-full max-h-[85vh] overflow-y-auto px-1">
      <VendorForm
        defaultValues={{
          name: initialData?.name || "",
          code: initialData?.code || "",
          ...(initialData || {}),
        }}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        submitLabel="Tạo & Chọn ngay"
        isModal
      />
    </div>
  );

  // When used as standalone modal with explicit open/onClose
  if (open !== undefined && onClose !== undefined) {
    return (
      <Dialog open={open} onOpenChange={(val) => !val && handleClose()}>
        <DialogContent className="w-[95vw] sm:max-w-5xl lg:max-w-7xl max-h-[92vh] flex flex-col p-4 sm:p-5">
          <DialogHeader className="sr-only">
            <DialogTitle>Thêm mới nhà cung cấp</DialogTitle>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  // When embedded in ComboboxFK which supplies its own Dialog
  return content;
}
